import {observer} from 'mobx-react-lite'
import React from 'react'
import {Lang} from 'yougile-lang'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Avatar} from '@components/common/base/avatar'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {ChatUtils} from '@extensions/chat-utils'
import {GroupChatProxy} from '@extensions/proxies/group-chat-proxy'
import {PersonalChatProxy} from '@extensions/proxies/personal-chat-proxy'
import {MenuIconButtonWithDropdown} from '../components/menu-icon-button-with-dropdown'
import {gotoGroup, gotoUser} from '../handlers/main-handlers'
import {useCreateGroup} from '../handlers/use-create-group'
import {useMainMenuStore} from '../main-menu-store'

const isGroupChat = (
  u: PersonalChatProxy | GroupChatProxy
): u is GroupChatProxy => {
  return 'users' in u
}

const getGetMsgCount = (u: PersonalChatProxy | GroupChatProxy): string => {
  const hasMentions = u.getNewMentionsAmount() > 0

  return u.newMessagesAmount > 0
    ? `${hasMentions ? '@ ' : ''}${u.newMessagesAmount}`
    : String(u.messagesCount)
}

const hasUnseenMsg = (
  usersAndGroups: (PersonalChatProxy | GroupChatProxy)[]
) => {
  return usersAndGroups.some(u => u.newMessagesAmount > 0)
}

interface ChatItem extends OptionsItem {
  newMessagesAmount?: number
}

export const ChatMenuItem = observer(() => {
  const [state] = useMainMenuStore()
  const {createGroup} = useCreateGroup()

  const toggleRightPanel = () => {
    ChatUtils.isChatExpanded()
      ? ChatUtils.closeChatIfOpened()
      : ChatUtils.openTaskChats()
  }

  const {usersAndGroups} = state

  const options: ChatItem[] = usersAndGroups.map(
    (u: PersonalChatProxy | GroupChatProxy) => {
      const {chatId: id, chatName: text} = u

      const common = {
        text,
        key: id,
        count: getGetMsgCount(u),
        highlightNumber: u.newMessagesAmount > 0,
        newMessagesAmount: u.newMessagesAmount || 0,
        getNewMentionsAmount: u.getNewMentionsAmount() || 0
      }

      if (isGroupChat(u)) {
        return {
          ...common,
          cb: () => gotoGroup(u),
          icon: 'IconMessage'
        }
      }

      return {
        ...common,
        cb: () => gotoUser(u),
        icon: <Avatar size={16} userId={u.userId} hideOutline />
      }
    }
  )

  options.sort(
    (a, b) => (b.newMessagesAmount ?? 0) - (a.newMessagesAmount ?? 0)
  )

  options.unshift({
    key: 'caption',
    text: Lang.msg.createGroupChat,
    selected: false,
    cb: () => {
      createGroup()
      void Tracking.send(TrackingEvent.leftPanelCreateNewGroupChatClick)
    },
    icon: 'IconPlus'
  })

  const notify = hasUnseenMsg(usersAndGroups)

  return (
    <MenuIconButtonWithDropdown
      options={options}
      icon="IconMessages"
      onClick={toggleRightPanel}
      notification={notify}
      className="mb-8"
    />
  )
})
